import { Badge, Box, Group, MultiSelect, Pagination, Stack, Text } from "@mantine/core";
import { HelpIcon, useHelpContext } from "@dehub/theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ReturnPreventionAlertApi } from "../../../apis";
import type {
  AlertSeverity,
  AlertStatus,
  AlertTrigger,
  IAlertListParams,
} from "../../../apis/return-prevention-alert";
import { TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { ReturnKPICards } from "../../../components/return/kpi-cards";
import { DeniedCard, ListState, SegmentedTabs } from "../../../components/ui";
import { PreventionAlertTable } from "../../../components/return/prevention-alert-table";
import { ALERT_HELP } from "../../../components/return/alert-action-modals";
import { triggerLabel } from "../../../components/return/alert-trigger-label";
import { severityLabel } from "../../../components/return/alert-severity-badge";

type TabKey = "ACTIVE" | "RESOLVED";

/** Count inside a segmented tab (same as Буцаалт, Хүргэлт удирдлага): filled on the active tab. */
function TabCount ({ count, active }: { count: number | undefined; active: boolean }){
  if (count == null) return null;
  return (
    <Badge size="md" radius="xl" color={active ? "dark" : "gray"} variant={active ? "filled" : "light"} ff="monospace">
      {count}
    </Badge>
  );
}

// «Идэвхтэй» = still open: not yet seen, seen but not closed, or muted until a date (O147). A seen
// alert can still be resolved or muted, so it stays in this tab. «Шийдвэрлэгдсэн» = RESOLVED only,
// the same set the KPI counts (O148).
const OPEN_STATUSES: AlertStatus[] = ["ACTIVE", "ACKNOWLEDGED", "MUTED"];

function buildParams(
  tab: TabKey,
  severities: AlertSeverity[],
  triggers: AlertTrigger[],
  page: number,
): IAlertListParams {
  return {
    status     : tab === "ACTIVE" ? OPEN_STATUSES : "RESOLVED",
    severity   : severities.length > 0 ? severities : undefined,
    triggerType: triggers.length > 0 ? triggers : undefined,
    page,
    limit      : 20,
  };
}

export function PreventionListPage() {
  const { t } = useTranslation("returns");
  const { hasAccess } = usePermission("RETURN_ANALYTICS_VIEW");
  const [activeTab, setActiveTab]   = useState<TabKey>("ACTIVE");
  const [severities, setSeverities] = useState<AlertSeverity[]>([]);
  const [triggers, setTriggers]     = useState<AlertTrigger[]>([]);
  const [page, setPage]             = useState(1);

  const BREADCRUMB = [
    { to: "/",        label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { label: t("prevention.list.breadcrumbPrevention") },
  ];

  const TABS: { value: TabKey; label: string }[] = [
    { value: "ACTIVE",   label: t("prevention.list.tabActive") },
    { value: "RESOLVED", label: t("prevention.list.tabResolved") },
  ];

  // O150 — the detector never produces INFO, so there is no INFO filter or KPI.
  const SEVERITY_OPTIONS: { value: AlertSeverity; label: string }[] = [
    { value: "WARNING",  label: severityLabel("WARNING") },
    { value: "CRITICAL", label: severityLabel("CRITICAL") },
  ];

  const TRIGGER_OPTIONS: { value: AlertTrigger; label: string }[] = [
    { value: "RETURN_RATE_THRESHOLD", label: triggerLabel("RETURN_RATE_THRESHOLD") },
    { value: "REASON_CONCENTRATION",  label: triggerLabel("REASON_CONCENTRATION") },
    { value: "SUDDEN_SPIKE",          label: triggerLabel("SUDDEN_SPIKE") },
    { value: "REPEAT_DEFECT",         label: triggerLabel("REPEAT_DEFECT") },
  ];

  const params = useMemo(
    () => buildParams(activeTab, severities, triggers, page),
    [activeTab, severities, triggers, page],
  );

  const { data, error, isLoading, mutate } = useSWR(
    hasAccess ? ["prevention.list", JSON.stringify(params)] : null,
    () => ReturnPreventionAlertApi.list(params),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  // KPI counts: open (ACTIVE + seen + muted) per severity, and RESOLVED. They use the same status
  // sets as the two tabs, so a KPI and its tab never disagree (O148).
  const { data: critData, mutate: mutateCrit }     = useSWR(hasAccess ? ["prevention.count", "CRITICAL"] : null, () => ReturnPreventionAlertApi.list({ status: OPEN_STATUSES, severity: "CRITICAL", limit: 1 }), { revalidateOnFocus: false });
  const { data: warnData, mutate: mutateWarn }     = useSWR(hasAccess ? ["prevention.count", "WARNING"] : null,  () => ReturnPreventionAlertApi.list({ status: OPEN_STATUSES, severity: "WARNING", limit: 1 }),  { revalidateOnFocus: false });
  const { data: resolvedData, mutate: mutateResolved } = useSWR(hasAccess ? ["prevention.count", "RESOLVED"] : null, () => ReturnPreventionAlertApi.list({ status: "RESOLVED", limit: 1 }),                          { revalidateOnFocus: false });

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab((value as TabKey) ?? "ACTIVE");
    setPage(1);
  }, []);

  const handleSeverityChange = useCallback((values: string[]) => {
    setSeverities(values as AlertSeverity[]);
    setPage(1);
  }, []);

  const handleTriggerChange = useCallback((values: string[]) => {
    setTriggers(values as AlertTrigger[]);
    setPage(1);
  }, []);

  // @dehub/help: the tab is the page variant; open alerts per severity drive the drawer's «Одоо» cards.
  const criticalCount = critData?.pagination.total;
  const warningCount = warnData?.pagination.total;
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ when: activeTab, vars: { criticalCount: criticalCount ?? 0, warningCount: warningCount ?? 0 } });
  }, [activeTab, criticalCount, warningCount, setHelpContext]);
  const tabCount = (tab: TabKey): number | undefined =>
    tab === "RESOLVED"
      ? resolvedData?.pagination.total
      : criticalCount == null || warningCount == null ? undefined : criticalCount + warningCount;

  const items      = data?.items ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;
  const total      = data?.pagination.total ?? 0;
  const filtered   = severities.length > 0 || triggers.length > 0;

  if (!hasAccess) {
    return (
      <PageLayout title={t("preventionPage.title")} breadcrumb={BREADCRUMB}>
        <DeniedCard permission="RETURN_ANALYTICS_VIEW" />
      </PageLayout>
    );
  }

  const footer = (
    <>
      <Text span ff="monospace" fz="var(--nw-fz-mono)">{items.length ? `${(page - 1) * (data?.pagination.limit ?? 20) + 1}-${(page - 1) * (data?.pagination.limit ?? 20) + items.length} / ${total}` : `0 / ${total}`}</Text>
      {totalPages > 1 ? <Pagination value={page} onChange={setPage} total={totalPages} size="sm" /> : <Box />}
    </>
  );

  return (
    <PageLayout
      title={t("preventionPage.title")}
      subTitle={t("preventionPage.subtitle")}
      breadcrumb={BREADCRUMB}
      loading={isLoading && !data}
    >
      <Stack gap="md">
        {/* Severity KPI counts (open per severity; RESOLVED) */}
        <ReturnKPICards
          kpis={[
            { label: severityLabel("CRITICAL"), value: critData?.pagination.total ?? "-", tone: "red" },
            { label: severityLabel("WARNING"), value: warnData?.pagination.total ?? "-", tone: "amber" },
            { label: t("prevention.list.kpiResolved"), value: resolvedData?.pagination.total ?? "-", tone: "green" },
          ]}
        />

        {/* Segmented tabs with counts + «?»: one list-header concept with the other lists. */}
        <Group gap="xs" wrap="nowrap">
          <SegmentedTabs
            value={activeTab}
            onChange={handleTabChange}
            data={TABS.map((tab) => ({
              value: tab.value,
              label: (
                <Group gap={6} wrap="nowrap">
                  {tab.label}
                  <TabCount count={tabCount(tab.value)} active={tab.value === activeTab} />
                </Group>
              ),
            }))}
          />
          <HelpIcon helpKey={`${ALERT_HELP}.types.tabs`} />
        </Group>

        <Group gap="sm" wrap="wrap">
          <MultiSelect
            placeholder={t("preventionTable.severity")}
            data={SEVERITY_OPTIONS}
            value={severities}
            onChange={handleSeverityChange}
            clearable
            w={220}
            size="sm"
          />
          <MultiSelect
            placeholder={t("preventionTable.pattern")}
            data={TRIGGER_OPTIONS}
            value={triggers}
            onChange={handleTriggerChange}
            clearable
            w={240}
            size="sm"
          />
        </Group>

        {error && !data ? (
          <TableFrame>
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          </TableFrame>
        ) : (
          <PreventionAlertTable
            items={items}
            onMutate={() => { mutate(); mutateCrit(); mutateWarn(); mutateResolved(); }}
            emptyTitle={filtered ? t("listTable.noResults") : t("preventionPage.empty")}
            emptyDescription={filtered ? t("listTable.noResultsHint") : t("preventionPage.emptyHint")}
            emptyWhen={filtered ? "noResults" : activeTab === "RESOLVED" ? "RESOLVED" : undefined}
            onClearFilters={() => { setSeverities([]); setTriggers([]); setPage(1); }}
            footer={items.length > 0 ? footer : undefined}
          />
        )}
      </Stack>
    </PageLayout>
  );
}
